<template>
  <v-app>
    <v-main>
      <v-container class="py-8">
        <!-- Search Section -->
        <v-card class="mb-6">
          <v-card-title class="primary white--text">
            <v-icon left dark>mdi-account-search</v-icon>
            Search Users
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="10">
                <v-text-field
                  v-model="searchQuery"
                  label="Search by ID, username,serial or email"
                  outlined
                  clearable
                  @keyup.enter="searchUsers"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center">
                <v-btn 
                  color="primary" 
                  large 
                  block
                  @click="searchUsers"
                  :loading="loading"
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Search
                </v-btn>
              </v-col>
            </v-row>

            <!-- Search Results -->
            <v-list v-if="searchResults.length" three-line class="elevation-1">
              <v-list-item 
                v-for="user in searchResults"
                :key="user.id"
                @click="loadUserDetails(user.id)"
              >
                <v-list-item-avatar color="grey lighten-2">
                  <v-icon>mdi-account</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ user.username }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ user.email }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle>
                    ID: {{ user.id }} | Balance: {{ user.balance }} XOF
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon>
                    <v-icon color="primary">mdi-chevron-right</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>

        <!-- User Details -->
        <v-card v-if="selectedUser" class="mb-6">
          <v-card-title class="info white--text d-flex justify-space-between">
            <div>
              <v-icon left dark>mdi-account-details</v-icon>
              User Details: {{ selectedUser.username }}
            </div>
            <v-btn icon dark @click="selectedUser = null">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <!-- User Info -->
            <v-row class="mb-4">
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="subtitle-1">Personal Information</v-card-title>
                  <v-card-text>
                    <v-simple-table dense>
                      <template v-slot:default>
                        <tbody>
                          <tr>
                            <td><strong>Username</strong></td>
                            <td>{{ selectedUser.username }}</td>
                          </tr>
                          <tr>
                            <td><strong>Email</strong></td>
                            <td>{{ selectedUser.email }}</td>
                          </tr>
                          <tr>
                            <td><strong>Phone</strong></td>
                            <td>{{ selectedUser.phone }}</td>
                          </tr>
                          <tr>
                            <td><strong>Affiliate Code</strong></td>
                            <td>{{ selectedUser.serial_no }}</td>
                          </tr>
                          <tr>
                            <td><strong>Account Type</strong></td>
                            <td>
                              <v-chip small :color="selectedUser.business_account ? 'primary' : 'secondary'">
                                {{ selectedUser.business_account ? 'Business' : 'Personal' }}
                              </v-chip>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="subtitle-1">Financial Information</v-card-title>
                  <v-card-text>
                    <v-simple-table dense>
                      <template v-slot:default>
                        <tbody>
                          <tr>
                            <td><strong>Balance</strong></td>
                            <td class="font-weight-bold">{{ selectedUser.balance }} XOF</td>
                          </tr>
                          <tr>
                            <td><strong>Total Commissions</strong></td>
                            <td>{{ selectedUser.commissions }} XOF</td>
                          </tr>
                          <tr>
                            <td><strong>Payment Status</strong></td>
                            <td>
                              <v-chip small :color="selectedUser.payment_status ? 'success' : 'warning'">
                                {{ selectedUser.status }}
                              </v-chip>
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Account Created</strong></td>
                            <td>{{ formatDate(selectedUser.created_at) }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Balance Update -->
            <v-card class="mb-6" outlined>
              <v-card-title class="warning darken-1 white--text subtitle-1">
                <v-icon left>mdi-cash-sync</v-icon>
                Update Balance
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="newBalance"
                      label="New Balance"
                      type="number"
                      outlined
                      prefix="XOF"
                      step="0.01"
                      min="0"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-textarea
                      v-model="balanceChangeReason"
                      label="Reason for update"
                      outlined
                      rows="1"
                      auto-grow
                    ></v-textarea>
                  </v-col>
                  <v-col cols="12" md="4" class="d-flex align-center">
                    <v-btn 
                      color="warning" 
                      block 
                      large
                      :disabled="!newBalance || newBalance == selectedUser.balance"
                      @click="updateUserBalance"
                      :loading="loading"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Update Balance
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Referral Relationships -->
            <v-row class="mb-6">
              <!-- Referrer -->
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="secondary white--text subtitle-1">
                    <v-icon left>mdi-account-supervisor</v-icon>
                    Referrer
                  </v-card-title>
                  <v-card-text v-if="selectedUser.referrer" class="text-center py-4">
                    <v-avatar size="80" color="grey lighten-2" class="mb-3">
                      <v-icon size="40">mdi-account</v-icon>
                    </v-avatar>
                    <h4>{{ selectedUser.referrer.name }}</h4>
                    <v-chip class="mt-2">
                      {{ selectedUser.referrer.serialNo }}
                    </v-chip>
                    <div class="mt-2">
                      <v-chip small>
                        {{ selectedUser.referrer.affiliateCount }} affiliate(s)
                      </v-chip>
                    </div>
                    <v-btn 
                      color="primary" 
                      small 
                      class="mt-3"
                      @click="loadUserDetails(selectedUser.referrer.id)"
                    >
                      <v-icon left small>mdi-eye</v-icon>
                      View Details
                    </v-btn>
                  </v-card-text>
                  <v-card-text v-else class="text-center py-4 text--disabled">
                    <v-icon size="60">mdi-account-question</v-icon>
                    <p class="mt-2 mb-0">No referrer</p>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Affiliates -->
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="success white--text subtitle-1 d-flex justify-space-between">
                    <div>
                      <v-icon left>mdi-account-group</v-icon>
                      Affiliates
                    </div>
                    <v-chip>{{ selectedUser.affiliates.length }}</v-chip>
                  </v-card-title>
                  <v-card-text>
                    <v-list v-if="selectedUser.affiliates.length" dense>
                      <v-list-item 
                        v-for="affiliate in selectedUser.affiliates"
                        :key="affiliate.id"
                      >
                        <v-list-item-avatar color="grey lighten-2">
                          <v-icon>mdi-account</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title>{{ affiliate.name }}</v-list-item-title>
                          <v-list-item-subtitle>{{ affiliate.email }}</v-list-item-subtitle>
                          <v-list-item-subtitle>
                            Commission: 
                            <span class="success--text font-weight-bold">
                              {{ affiliate.commission }} XOF
                            </span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn 
                            icon 
                            small
                            @click="loadUserDetails(affiliate.id)"
                          >
                            <v-icon color="success">mdi-arrow-right</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                    <div v-else class="text-center py-4 text--disabled">
                      <v-icon size="60">mdi-account-multiple-remove</v-icon>
                      <p class="mt-2 mb-0">No affiliates</p>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Commission History -->
            <v-card outlined>
              <v-card-title class="purple white--text subtitle-1">
                <v-icon left>mdi-history</v-icon>
                Commission History
              </v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="commissionHeaders"
                  :items="selectedUser.commission_history"
                  :items-per-page="5"
                  class="elevation-1"
                >
                  <template v-slot:item.amount="{ item }">
                    <span class="font-weight-bold success--text">{{ item.amount }} XOF</span>
                  </template>
                  <template v-slot:item.affiliate="{ item }">
                    <span v-if="item.user && item.user.id">
                      {{ item.user.name }}
                    </span>
                    <span v-else class="text--disabled">
                      {{ item.affiliateName || 'Unknown' }}
                    </span>
                  </template>
                  <template v-slot:item.code="{ item }">
                    <v-chip v-if="item.user && item.user.affiliateCode" small>
                      {{ item.user.affiliateCode }}
                    </v-chip>
                    <span v-else class="text--disabled">
                      {{ item.affiliateSerialNo || 'N/A' }}
                    </span>
                  </template>
                  <template v-slot:item.validated="{ item }">
                    <v-chip :color="item.validated ? 'success' : 'grey'" small dark>
                      {{ item.validated ? 'Completed' : 'Pending' }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>

        <!-- Audit Log Button -->
        <v-btn 
          color="info" 
          class="mb-6" 
          @click="auditDialog = true"
          :loading="auditLoading"
        >
          <v-icon left>mdi-clipboard-text</v-icon>
          View Balance Audit Log
        </v-btn>

        <!-- Audit Log Dialog -->
        <v-dialog v-model="auditDialog" max-width="900">
          <v-card>
            <v-card-title class="info white--text">
              <v-icon left>mdi-clipboard-text</v-icon>
              Balance Audit Log
            </v-card-title>
            <v-card-text>
              <v-data-table
                :headers="auditHeaders"
                :items="auditLog"
                :loading="auditLoading"
                loading-text="Loading audit log..."
                class="elevation-1"
              >
              <template v-slot:item.manager="{ item }">
                  <div>
                    <div>{{ item.manager_name }}</div>
                    <div class="caption">({{ item.manager_serial }})</div>
                  </div>
                </template>
                <template v-slot:item.date="{ item }">
                  {{ formatDate(item.date) }}
                </template>
                <template v-slot:item.change="{ item }">
                  <span :class="item.change > 0 ? 'success--text' : 'error--text'">
                    {{ item.change > 0 ? '+' : '' }}{{ item.change }} XOF
                  </span>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn 
                    icon 
                    small
                    @click="viewUser(item.user_id)"
                  >
                    <v-icon small color="info">mdi-account-eye</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="secondary" @click="auditDialog = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        

        
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import axios from 'axios';

export default {
  name: 'UserManager',
  data: () => ({
    searchQuery: '',
    searchResults: [], 
    selectedUser: null,
    newBalance: null,
    confirmationDialog: false,
    balanceChangeReason: '',
    loading: false,
    auditDialog: false,
    auditLog: [],
    auditLoading: false,
    
    // Table Headers
    commissionHeaders: [
      { text: 'Date', value: 'date' },
      { text: 'Amount', value: 'amount' },
      { text: 'Affiliate', value: 'affiliate' },
      { text: 'Code', value: 'code' },
      { text: 'Status', value: 'validated' },
    ],
    
    auditHeaders: [
      { text: 'Date', value: 'date', width: '160px' },
      { 
        text: 'Manager', 
        value: 'manager',
        width: '200px',
        sortable: false
      },
      { text: 'User', value: 'username', width: '150px' },
      { text: 'Old Balance', value: 'old_balance', align: 'right', width: '120px' },
      { text: 'New Balance', value: 'new_balance', align: 'right', width: '120px' },
      { text: 'Change', value: 'change', align: 'right', width: '120px' },
      { text: 'Reason', value: 'reason' },
      { 
        text: 'Actions', 
        value: 'actions', 
        width: '100px', 
        align: 'center',
        sortable: false
      },
    ]
  }),
  mounted() {
    // Initialize loading state
    this.loading = false;
    this.auditLoading = false;
    this.searchResults = [];
    this.selectedUser = null;
    this.newBalance = null;
    this.balanceChangeReason = '';

  },
  methods: {
    async searchUsers() {
      if (!this.searchQuery) return;
      
      try {
        this.loading = true;
        const response = await axios.get('/api/v1/manager/search-users/', {
          params: { q: this.searchQuery }
        });
        this.searchResults = response.data.results;
      } catch (error) {
        console.error('Search error:', error);
        this.$toast.error('Failed to search users');
      } finally {
        this.loading = false;
      }
    },
    
    async loadUserDetails(userId) {
      try {
        this.loading = true;
         const response = await axios.get(`/api/v1/manager/user-details/${userId}/`);
        
        // Process commission history
        this.selectedUser = {
          ...response.data,
          commission_history: response.data.commission_history.map(comm => ({
            ...comm,
            affiliateName: comm.description ? 
              comm.description.split(' ').pop() : 
              comm.user?.name || 'Unknown'
          }))
        };
        
        // Initialize balance form
        this.newBalance = this.selectedUser.balance;
        this.searchResults = [];
      } catch (error) {
        console.error('Error loading user details:', error);
        this.$toast.error('Failed to load user details');
      } finally {
        this.loading = false;
      }
    },
    
    async updateUserBalance() {
      if (!confirm(`Confirm balance update for ${this.selectedUser.username} to ${this.newBalance} XOF?`)) {
        return;
      }
      
      try {
        this.loading = true;
        const response = await axios.post('/api/v1/manager/update-balance/', {
          user_id: this.selectedUser.id,
          new_balance: parseFloat(this.newBalance),
          reason: this.balanceChangeReason
        });
        
        // Update local data
        this.selectedUser.balance = parseFloat(this.newBalance);
        
        // Add to transaction history
        this.selectedUser.commission_history.unshift({
          id: Date.now(), // Temporary ID
          amount: response.data.balance_change,
          date: new Date().toISOString(),
          validated: true,
          description: `Balance adjustment: ${this.balanceChangeReason}`,
          affiliateName: 'System',
          user: {
            name: 'Administrator'
          }
        });
        
        // Reset form
        this.balanceChangeReason = '';
        
        this.$toast.success('Balance updated successfully!');
      } catch (error) {
        console.error('Update error:', error);
        let errorMsg = 'Update failed';
        if (error.response?.data?.error) {
          errorMsg = error.response.data.error;
        }
        this.$toast.error(errorMsg);
      } finally {
        this.loading = false;
      }
    },
    
    async loadAuditLog() {
      try {
        this.auditLoading = true;
        const response = await axios.get('/api/v1/manager/audit-log/balance/');
        this.auditLog = response.data;
      } catch (error) {
        console.error('Failed to load audit log:', error);
        this.$toast.error('Failed to load audit log');
        this.auditLog = [];
      } finally {
        this.auditLoading = false;
      }
    },
    
    viewUser(userId) {
      this.auditDialog = false;
      this.loadUserDetails(userId);
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  },
  watch: {
    auditDialog(val) {
      if (val) {
        this.loadAuditLog();
      }
    }
  }
};
</script>

<style scoped>
.purple {
  background-color: #6f42c1 !important;
  border-color: #6f42c1 !important;
}

.v-card__title.info {
  background-color: #17a2b8 !important;
}

.v-card__title.success {
  background-color: #28a745 !important;
}

.v-card__title.warning {
  background-color: #ffc107 !important;
}

.v-card__title.secondary {
  background-color: #6c757d !important;
}

.v-card__title.purple {
  background-color: #6f42c1 !important;
}

/* Custom scrollbar */
.v-data-table ::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.v-data-table ::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.v-data-table ::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.v-data-table ::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Fix for v-btn in tables */
.v-data-table .v-btn {
  margin: 0;
}
</style>